import type { CSSProperties } from "react";
import { SxProps, Theme } from "@mui/material";
import colors from "../../shared/constants/color";
import { textXS } from "../../shared/constants";
import type { WiserThemeTokens } from "../../shared/theme/wiserTheme";
import { getHrCommentCardSx } from "../../shared/theme/wiserThemeHelpers";


export const twoColumnGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 2fr",
  gap: 3,
};

export const boardContainer = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  border: `1px solid ${colors.neutral.N30}`,
  borderRadius: "8px",
  p: 2,
};

export const aiContainer = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  border: "1px solid transparent",
  borderRadius: "8px",
  background: `linear-gradient(white, white) padding-box, ${colors.aiLinearGradient} border-box`,
  p: 2,
};

export const itemContainer = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  border: `1px solid ${colors.neutral.N30}`,
  borderRadius: "8px",
  backgroundColor: colors.neutral.N10,
  p: 2,
  position: "relative",
};

export const cardActionsContainer = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 1,
  mt: 3,
};

export const deleteIconContainer = {
  position: "absolute",
  top: "12px",
  right: "12px",
  cursor: "pointer",
};

// ===== Shared layout =====

export const centeredContentSx: SxProps<Theme> = {
  display: "flex",
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
};

export const centeredContentFillSx: SxProps<Theme> = {
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const centeredContentPy4Sx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  py: 4,
  flex: 1,
};

export const centeredEmptyStateSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  py: 4,
  textAlign: "center",
};

export const centeredMinHeightSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "200px",
};

export const columnGap8Sx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

export const columnGap12Sx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

export const rowAlignCenterGap8Sx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

export const rowAlignCenterGap8WrapSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
};

export const rowAlignCenterGap16WrapSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
};

export const iconCheckWrapSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
};

export const getIcon14Style = (iconFilter?: string): CSSProperties => ({
  width: "14px",
  height: "14px",
  filter: iconFilter,
});

export const getTagChipSx = (bg: string): SxProps<Theme> => ({
  backgroundColor: bg,
  borderRadius: "3px",
  padding: "2px 6px",
  width: "fit-content",
});

// ===== InterviewAssistant page layout =====

export const pageLoadingSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "calc(100vh - 214px)",
};

export const threeColumnLayoutSx: SxProps<Theme> = {
  mt: 2,
  display: "flex",
  gap: "24px",
  height: "calc(100vh - 214px)",
  minHeight: "600px",
  overflowY: "hidden",
  minWidth: 0,
};

export const pageColumnSx: SxProps<Theme> = {
  flex: "1 1 0",
  minWidth: 0,
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  overflow: "hidden",
};

export const pageColumnWideSx: SxProps<Theme> = {
  ...pageColumnSx,
  flex: "2 1 0",
};

export const cardFlexAutoSx: SxProps<Theme> = {
  flex: "0 1 auto",
  minHeight: 0,
};

export const cardFlex1Sx: SxProps<Theme> = {
  flex: "1 1 0",
  minHeight: 0,
};

export const cardFlex2Sx: SxProps<Theme> = {
  flex: "2 1 0",
  minHeight: 0,
};

export const cardFlex3Sx: SxProps<Theme> = {
  flex: "3 1 0",
  minHeight: 0,
};

// ===== CandidateHeader =====

export const candidateHeaderRootSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "space-between",
};

export const candidateHeaderLeftSx: SxProps<Theme> = {
  display: "flex",
  gap: "24px",
  alignItems: "center",
  flexWrap: "wrap",
  rowGap: "8px",
};

export const candidateHeaderFieldsSx: SxProps<Theme> = {
  display: "flex",
  gap: "8px",
  alignItems: "center",
};

export const headerAutocompleteInputSx: SxProps<Theme> = {
  width: "auto",
  "& .MuiInputBase-root": {
    width: "auto",
  },
};

export const getHeaderAutocompleteSx = (
  bg: string,
  text: string,
  hasError?: boolean,
  maxWidth?: number | string,
): SxProps<Theme> => ({
  display: "inline-flex",
  width: "auto",
  minWidth: "fit-content",
  maxWidth: maxWidth ?? "none",
  "& .MuiAutocomplete-inputRoot": {
    width: "auto",
    minWidth: "fit-content",
    maxWidth: "100%",
    backgroundColor: bg,
    borderRadius: "3px",
    height: "28px",
    padding: "0 !important",
    "& .MuiOutlinedInput-notchedOutline": {
      border: hasError ? `1px solid ${colors.red.R500}` : "none",
    },
    "& .MuiAutocomplete-input": {
      ...textXS(500, text),
      padding: "4px 8px !important",
      width: "auto !important",
      minWidth: "0 !important",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& .MuiAutocomplete-endAdornment": {
      "& .MuiSvgIcon-root": {
        color: text,
        fontSize: "18px",
      },
    },
  },
  "& .MuiAutocomplete-popupIndicator": {
    color: text,
  },
  "& .MuiAutocomplete-clearIndicator": {
    color: text,
  },
});

export const getManuelScoreFieldSx = (
  bg: string,
  text: string,
  hasError?: boolean,
): SxProps<Theme> => ({
  display: "inline-flex",
  width: "118px",
  minWidth: "118px",
  "& .MuiOutlinedInput-root": {
    width: "118px",
    minWidth: "118px",
    backgroundColor: bg,
    borderRadius: "3px",
    height: "28px",
    padding: "0 !important",
    "& .MuiOutlinedInput-notchedOutline": {
      border: hasError ? `1px solid ${colors.red.R500}` : "none",
    },
    "& .MuiOutlinedInput-input": {
      ...textXS(500, text),
      padding: "4px 8px !important",
      width: "100% !important",
      minWidth: "0 !important",
      textAlign: "left",
    },
  },
});

export const getManuelScoreColors = (
  darkModeActive: boolean,
  tokens: WiserThemeTokens,
) => ({
  bg: darkModeActive ? "rgba(7, 71, 166, 0.22)" : colors.blue.B50,
  text: darkModeActive ? tokens.textPrimary : colors.blue.B500,
});

// ===== Shared tags =====

export const workingModelTagGroupSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "4px",
  flexWrap: "wrap",
  width: "fit-content",
};

export const getWorkingModelChipSx = (bg: string): SxProps<Theme> => ({
  backgroundColor: bg,
  borderRadius: "3px",
  padding: "0px 4px",
  width: "fit-content",
});

// ===== AIScoreCircle =====

export const getAiScoreCircleSx = (size: number): SxProps<Theme> => ({
  width: `${size}px`,
  height: `${size}px`,
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const aiScoreSvgStyle: CSSProperties = {
  transform: "rotate(-90deg)",
  position: "absolute",
  top: 0,
  left: 0,
};

export const getAiScoreTextSx = (size: number): SxProps<Theme> => ({
  "&&": {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 600,
    fontStyle: "normal",
    fontSize: size <= 30 ? "12px" : "18px",
    lineHeight: size <= 30 ? "16px" : "24px",
    letterSpacing: "0%",
    color: "#1968B0",
  },
});

// ===== Question list cards =====

export const getAiQuestionTabsSx = (
  darkModeActive: boolean,
  tokens: WiserThemeTokens,
): SxProps<Theme> => ({
  display: "flex",
  borderBottom: `1px solid ${darkModeActive ? tokens.border : colors.neutral.N40}`,
  gap: "4px",
});

export const getAiQuestionTabSx = (
  isActive: boolean,
  darkModeActive: boolean,
  tokens: WiserThemeTokens,
): SxProps<Theme> => ({
  px: "12px",
  py: "6px",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: isActive ? 600 : 400,
  color: isActive
    ? darkModeActive
      ? tokens.accent
      : colors.blue.B200
    : darkModeActive
      ? tokens.textMuted
      : colors.neutral.N500,
  borderBottom: isActive
    ? `2px solid ${darkModeActive ? tokens.accent : colors.blue.B200}`
    : "2px solid transparent",
  transition: "all 0.15s",
  userSelect: "none",
});

// ===== Position detail card =====

export const matchEvalHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

export const matchEvalDescriptionSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flex: 1,
};

export const matchEvalEmptySx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  py: 3,
  textAlign: "center",
};

// ===== AI recommended positions =====

export const getAiRecommendedRootSx = (
  height: number,
  minWidth?: string | number,
): SxProps<Theme> => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  minHeight: "30vh",
  maxHeight: "68.5vh",
  height: `${height}px`,
  overflow: "hidden",
  flex: "0 0 auto",
  marginTop: "auto",
  width: "100%",
  ...(minWidth != null ? { minWidth } : {}),
});

export const getResizeHandleSx = (
  isDragging: boolean,
  darkModeActive: boolean,
  tokens: WiserThemeTokens,
  variant: "ai" | "hr",
): SxProps<Theme> => {
  const idleBg =
    variant === "ai"
      ? darkModeActive
        ? tokens.border
        : `${colors.aiGradientStart}30`
      : darkModeActive
        ? tokens.border
        : colors.yellow.Y100;
  const activeBg =
    variant === "ai"
      ? darkModeActive
        ? tokens.hoverBg
        : colors.blue.B200
      : darkModeActive
        ? tokens.hoverBg
        : colors.yellow.Y200;

  return {
    position: "absolute",
    top: 0,
    right: 0,
    width: "20px",
    height: "20px",
    cursor: "ns-resize",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: isDragging ? activeBg : idleBg,
    borderTopRightRadius: "6px",
    borderBottomLeftRadius: "6px",
    transition: "background-color 0.2s",
    zIndex: variant === "ai" ? 1000 : 10,
    "&:hover": {
      backgroundColor: activeBg,
    },
  };
};

export const getResizeHandleIconSx = (
  darkModeActive: boolean,
  tokens: WiserThemeTokens,
  variant: "ai" | "hr",
): SxProps<Theme> => ({
  fontSize: "16px",
  transform: "scale(0.7)",
  color:
    variant === "ai"
      ? darkModeActive
        ? tokens.accent
        : colors.blue.B300
      : darkModeActive
        ? tokens.textMuted
        : colors.yellow.Y500,
});

export const aiRecommendedCardSx: SxProps<Theme> = {
  height: "100%",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};

export const aiRecommendedLoadingSx: SxProps<Theme> = {
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
};

export const aiPositionRowContentSx: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  minWidth: 0,
};

export const getAiPositionDividerSx = (
  darkModeActive: boolean,
  tokens: WiserThemeTokens,
): SxProps<Theme> => ({
  marginTop: "12px",
  borderColor: darkModeActive ? tokens.border : colors.neutral.N30,
});

export const cursorHelpSx: SxProps<Theme> = { cursor: "help" };
export const flexShrink0Sx: SxProps<Theme> = { flexShrink: 0 };

// ===== HR comment card =====

// ===== HR comment card (upstream 366a5d0) =====
export const getHrCommentRootSx = (
  darkModeActive: boolean,
  height: number,
) => ({
  ...(getHrCommentCardSx(darkModeActive) as object),
  height: `${height}px`,
  flexDirection: "column",
});

export const getHrCommentQuillWrapperSx = (
  darkModeActive: boolean,
  tokens: WiserThemeTokens,
): SxProps<Theme> => {
  const borderColor = darkModeActive ? tokens.border : colors.yellow.Y100;
  return {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    "& .quill": {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      height: "100%",
    },
    "& .ql-toolbar.ql-snow": {
      border: "none",
      borderBottom: `1px solid ${borderColor}`,
      backgroundColor: "transparent",
      paddingRight: "28px",
      borderTopLeftRadius: "8px",
      borderTopRightRadius: "8px",
    },
    "& .ql-container.ql-snow": {
      border: "none",
      height: "auto !important",
      flex: 1,
      overflow: "auto",
      fontFamily:
        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: "13px",
      backgroundColor: "transparent",
      color: darkModeActive ? tokens.textPrimary : colors.neutral.N700,
    },
    "& .ql-editor": {
      minHeight: "100%",
      padding: "12px",
      color: darkModeActive ? tokens.textPrimary : colors.neutral.N700,
    },
    "& .ql-editor.ql-blank::before": {
      color: darkModeActive ? tokens.textMuted : colors.neutral.N200,
      fontStyle: "normal",
    },
    "& .ql-snow .ql-stroke": {
      stroke: darkModeActive ? tokens.textSecondary : undefined,
    },
    "& .ql-snow .ql-fill": {
      fill: darkModeActive ? tokens.textSecondary : undefined,
    },
    "& .ql-snow .ql-picker": {
      color: darkModeActive ? tokens.textPrimary : undefined,
    },
    "& .ql-snow .ql-picker-options": {
      backgroundColor: darkModeActive ? tokens.surface : undefined,
      borderColor: darkModeActive ? tokens.border : undefined,
    },
    "& .ql-editor a": {
      color: darkModeActive ? tokens.accent : colors.blue.B300,
    },
    "& .ql-container::-webkit-scrollbar": {
      width: "4px",
    },
    "& .ql-container::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "& .ql-container::-webkit-scrollbar-thumb": {
      background: darkModeActive ? tokens.textMuted : colors.neutral.N40,
      borderRadius: "3px",
      "&:hover": {
        background: darkModeActive ? tokens.textSecondary : colors.neutral.N50,
      },
    },
  };
};

export const hrCommentFooterSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  padding: "8px",
  flexShrink: 0,
};

// ===== Modals =====

export const modalMaxHeightSx = {
  maxHeight: "90vh",
  mt: "80px",
};

export const positionDetailModalSx = {
  maxHeight: "90vh",
  height: "80vh",
  mt: "80px",
};

export const modalHeaderRowSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

export const modalHeaderLeftSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

export const modalHeaderTitleRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

export const modalHeaderMetaRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 3,
};

export const modalHeaderMetaWrapSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "15px",
  flexWrap: "wrap",
};

export const modalHeaderTagsSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
};

export const modalHeaderInfoRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

export const modalHeaderRightSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  justifyContent: "flex-end",
  alignItems: "flex-end",
};

export const getModalDividerSx = (
  darkModeActive: boolean,
  tokens: WiserThemeTokens,
): SxProps<Theme> => ({
  mt: 3,
  mb: 3,
  borderColor: darkModeActive ? tokens.border : undefined,
});

export const breakWordSx: SxProps<Theme> = {
  overflowWrap: "break-word",
  wordBreak: "break-word",
};

export const gridFullWidthSx: SxProps<Theme> = {
  gridColumn: "1 / -1",
};

export const checkboxNoPaddingSx: SxProps<Theme> = {
  padding: 0,
};

export const relatedPersonsErrorSx: SxProps<Theme> = {
  p: 2,
  color: "error.main",
};

export const addPositionGridWrapSx: SxProps<Theme> = {
  margin: "-16px -24px",
};

export const addPositionTechGroupCellSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  py: 1,
};

export const positionDetailTabSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: "16px",
  fontFamily: "Inter",
};

export const getCreatedByTextSx = (
  darkModeActive: boolean,
  tokens: WiserThemeTokens,
): SxProps<Theme> => ({
  ...textXS(
    400,
    darkModeActive ? tokens.textSecondary : colors.neutral.N500,
  ),
  textAlign: "right",
});
